import {SelectChangeEvent} from '@mui/material';
import {GridRowParams, GridRowSelectionModel, GridSortModel} from '@mui/x-data-grid';
import {map, toString} from 'lodash';
import {FC, useEffect} from 'react';
import {useRecoilState, useRecoilStateLoadable, useRecoilValue, useSetRecoilState} from 'recoil';
import {useEventBus} from '../../../hooks/useEventBus';
import {LoadableHelpers} from '../../../shared/helpers/loadableHelpers';
import {
  companiesQueryTrigger,
  companiesSelectedState,
  companiesSortQueryTrigger,
  companiesState,
  companyPageable,
  currentCompanyIdState,
} from '../../../store/company/atoms';
import {companiesQuery} from '../../../store/company/selectors';
import {Table} from '../../Table';
import {columns} from './TableBody/columns';
import TableToolbar from './TableToolbar/TableToolbar';
import {useNavigate} from 'react-router-dom';

const CompaniesTable: FC = () => {
  const navigate = useNavigate();
  const bus = useEventBus();
  const [loadable, setCompanies] = useRecoilStateLoadable(companiesQuery);
  const setCurrentCompanyId = useSetRecoilState(currentCompanyIdState);
  const triggerQuery = useSetRecoilState(companiesQueryTrigger);
  const {content, totalElements, totalPages} = useRecoilValue(companiesState);
  const [selected, setSelected] = useRecoilState(companiesSelectedState);
  const [sort, setSort] = useRecoilState(companiesSortQueryTrigger);
  const [pageable, setPageable] = useRecoilState(companyPageable);

  const handleSelect = (ids: GridRowSelectionModel) => {
    setSelected((prev) => ({...prev, [pageable.page]: map(ids, toString)}));
  };

  const handleChangePage = (_: unknown, page: number) => {
    setPageable((prev) => ({...prev, page}));
  };

  const handlePageSizeChange = (e: SelectChangeEvent<number>) => {
    setPageable({page: 0, size: e.target.value as number});
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSort([...model]);
  };

  const handleRowClick = ({row}: GridRowParams) => {
    setCurrentCompanyId(row.id);
    navigate(`/customers/${row.id}`);
  };

  useEffect(() => {
    LoadableHelpers.handle(loadable, {success: setCompanies, error: bus?.emit});

    return () => {
      if (LoadableHelpers.isLoading(loadable)) return;
      triggerQuery((prev) => prev + 1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadable.state]);

  return (
    <>
      <TableToolbar isLoading={LoadableHelpers.isLoading(loadable)} />
      <Table
        name="companies"
        loading={LoadableHelpers.isLoading(loadable)}
        rows={content}
        columns={columns}
        sortModel={sort}
        pageSize={pageable.size}
        totalPages={totalPages}
        totalCount={totalElements}
        rowSelectionModel={selected[pageable.page]}
        currentPage={pageable.page}
        startPageFromZero
        checkboxSelection
        paddingProp={68}
        onRowClick={handleRowClick}
        onSortModelChange={handleSortModelChange}
        onPageSizeChange={handlePageSizeChange}
        onPaginationChange={handleChangePage}
        onRowSelectionModelChange={handleSelect}
      />
    </>
  );
};

export default CompaniesTable;
